/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Minimal DER (ASN.1) TLV reader — just enough to walk an X.509 certificate.
 */

export interface Tlv {
    /** Identifier octet (class/constructed/tag). */
    tag: number
    /** Absolute offset of the tag byte. */
    start: number
    /** Absolute offset where the content (value) begins. */
    contentStart: number
    /** Absolute offset one past the content. */
    contentEnd: number
    /** Absolute offset one past the whole TLV (== contentEnd). */
    end: number
}

/** Thrown on adversarial/truncated DER so a parse failure is a deterministic reject. */
export class DerError extends Error {}

/**
 * Reads one TLV at `off`. Supports short and long-form definite lengths, and is
 * bounds-checked (X509-1): a header or content that runs past the buffer, an
 * indefinite length (`0x80`, not allowed in DER), or an absurd long-form byte
 * count throws {@link DerError} rather than silently mis-parsing to `NaN`/empty
 * slices. `parseCertificate` catches this and yields a deterministic Invalid.
 */
export function readTlv (u8: Uint8Array, off: number): Tlv {
    if (off < 0 || off + 2 > u8.length) throw new DerError('truncated TLV header')
    const tag = u8[off]
    let p = off + 1
    let len = u8[p++]
    if (len & 0x80) {
        const n = len & 0x7f
        if (n === 0) throw new DerError('indefinite length not allowed in DER')
        if (n > 4) throw new DerError('unsupported long-form length')
        if (p + n > u8.length) throw new DerError('truncated long-form length')
        len = 0
        for (let i = 0; i < n; i++) len = len * 256 + u8[p++]
    }
    const contentStart = p
    const contentEnd = p + len
    if (contentEnd > u8.length) throw new DerError('TLV content exceeds buffer')
    return { tag, start: off, contentStart, contentEnd, end: contentEnd }
}

/** Reads the children TLVs of a constructed value spanning `[start, end)`. */
export function readChildren (u8: Uint8Array, start: number, end: number): Tlv[] {
    const out: Tlv[] = []
    let off = start
    while (off < end) {
        const t = readTlv(u8, off)
        out.push(t)
        off = t.end
    }
    return out
}

/** The raw bytes of a TLV including its tag+length header. */
export function tlvBytes (u8: Uint8Array, t: Tlv): Uint8Array {
    return u8.subarray(t.start, t.end)
}

/** The content bytes of a TLV (value only). */
export function content (u8: Uint8Array, t: Tlv): Uint8Array {
    return u8.subarray(t.contentStart, t.contentEnd)
}

/** Decodes an OBJECT IDENTIFIER's content bytes to dotted-decimal. */
export function decodeOid (bytes: Uint8Array): string {
    if (bytes.length === 0) return ''
    const parts: number[] = []
    const first = bytes[0]
    parts.push(Math.floor(first / 40), first % 40)
    let value = 0
    for (let i = 1; i < bytes.length; i++) {
        value = value * 128 + (bytes[i] & 0x7f)
        if (!(bytes[i] & 0x80)) {
            parts.push(value)
            value = 0
        }
    }
    return parts.join('.')
}

/**
 * Decodes a DER INTEGER expected to be a small non-negative value (e.g.
 * pathLenConstraint) to a JS number, reading ALL content bytes (not just the
 * first) and rejecting a negative (high-bit-set) or absurdly large value as
 * `undefined` (X509-6). A single stripped `0x00` sign pad is tolerated.
 */
export function decodeSmallNonNegInt (bytes: Uint8Array): number | undefined {
    if (bytes.length === 0) return undefined
    // A DER INTEGER is negative iff the high bit of its FIRST byte is set — test that
    // before stripping, so a leading 0x00 (legitimate sign-padding for e.g. 128) is
    // read as positive, not mistaken for a negative value.
    if (bytes[0] & 0x80) return undefined
    let b = bytes
    if (b[0] === 0x00) b = b.subarray(1) // strip the sign-padding byte
    if (b.length > 4) return undefined // implausibly large for pathLen etc.
    let v = 0
    for (const x of b) v = v * 256 + x
    return v
}

/** Decodes an INTEGER's content bytes to an unsigned decimal string (big-int safe). */
export function decodeIntegerDecimal (bytes: Uint8Array): string {
    // strip a leading sign/padding zero, then build the decimal via BigInt
    let hex = ''
    for (const b of bytes) hex += b.toString(16).padStart(2, '0')
    if (hex === '') return '0'
    return BigInt('0x' + hex).toString(10)
}
